import React, { useEffect, useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Check, DollarSign, Calendar, Search, ChevronUp, ChevronDown, FileText, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

export const PaymentReminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPaid, setFilterPaid] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'due_date', direction: 'asc' });

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  useEffect(() => {
    fetchReminders();
  }, [currentYear, currentMonth]);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reminders/payments/month/${currentYear}/${currentMonth}`);
      setReminders(response.data || []);
    } catch (error) {
      console.error('Error fetching payment reminders:', error);
      toast.error('Error al cargar los recordatorios de pago');
      setReminders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}/paid`);
      toast.success('Pago marcado como realizado');
      fetchReminders();
    } catch (error) {
      toast.error('Error al marcar el pago');
    }
  };

  const handleUnmarkPaid = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}/unpaid`);
      toast.success('Pago desmarcado');
      fetchReminders();
    } catch (error) {
      toast.error('Error al desmarcar el pago');
    }
  };

  const handlePreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedReminders = useMemo(() => {
    let filtered = [...reminders];
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(reminder => {
        const policyNumber = reminder.policy?.policy_number?.toLowerCase() || '';
        const insurer = reminder.policy?.insurer?.toLowerCase() || '';
        const contractorName = reminder.policy?.contractor?.name?.toLowerCase() || 
                               reminder.policy?.contractor?.toLowerCase() || '';
        
        return policyNumber.includes(searchLower) ||
               insurer.includes(searchLower) ||
               contractorName.includes(searchLower);
      });
    }
    
    if (filterPaid === 'paid') {
      filtered = filtered.filter(r => r.is_paid);
    } else if (filterPaid === 'unpaid') {
      filtered = filtered.filter(r => !r.is_paid);
    }
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortConfig.key === 'policy_number') {
        aVal = a.policy?.policy_number || '';
        bVal = b.policy?.policy_number || '';
      } else if (sortConfig.key === 'contractor') {
        aVal = a.policy?.contractor?.name || a.policy?.contractor || '';
        bVal = b.policy?.contractor?.name || b.policy?.contractor || '';
      } else if (sortConfig.key === 'insurer') {
        aVal = a.policy?.insurer || '';
        bVal = b.policy?.insurer || '';
      } else if (sortConfig.key === 'due_date') {
        aVal = new Date(a.due_date).getTime();
        bVal = new Date(b.due_date).getTime();
      } else if (sortConfig.key === 'amount') {
        aVal = a.amount || 0;
        bVal = b.amount || 0;
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }
      
      if (aVal === bVal) return 0;
      const comparison = aVal < bVal ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [reminders, searchTerm, filterPaid, sortConfig]);

  const stats = useMemo(() => {
    const pending = reminders.filter(r => !r.is_paid);
    const paid = reminders.filter(r => r.is_paid);
    const totalPending = pending.reduce((sum, r) => sum + (r.amount || 0), 0);
    const totalPaid = paid.reduce((sum, r) => sum + (r.amount || 0), 0);
    
    return {
      pending: pending.length,
      paid: paid.length,
      total: reminders.length,
      totalPending,
      totalPaid,
      totalAmount: totalPending + totalPaid
    };
  }, [reminders]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando recordatorios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Recordatorios de Pago</h1>
            <p className="text-slate-600">Gestiona los pagos de tus pólizas</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePreviousMonth}
              variant="outline"
              size="sm"
            >
              <ChevronLeft size={20} />
            </Button>
            
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg">
              <Calendar size={18} />
              <span className="font-medium">
                {months[currentMonth - 1]} {currentYear}
              </span>
            </div>

            <Button
              onClick={handleNextMonth}
              variant="outline"
              size="sm"
            >
              <ChevronRight size={20} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Pagos Pendientes
              </CardTitle>
              <DollarSign className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.pending}</div>
              <p className="text-xs text-slate-600 mt-1">{formatCurrency(stats.totalPending, 'MXN')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Pagos Realizados
              </CardTitle>
              <Check className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
              <p className="text-xs text-slate-600 mt-1">{formatCurrency(stats.totalPaid, 'MXN')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Total del Mes
              </CardTitle>
              <Calendar className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Monto Total
              </CardTitle>
              <DollarSign className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(stats.totalAmount, 'MXN')}</div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
            <Input
              placeholder="Buscar por póliza, cliente o aseguradora..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterPaid} onValueChange={setFilterPaid}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Estado de pago" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los pagos</SelectItem>
              <SelectItem value="unpaid">Pendientes</SelectItem>
              <SelectItem value="paid">Realizados</SelectItem>
            </SelectContent>
          </Select>

          <div className="text-sm text-slate-600">
            {filteredAndSortedReminders.length} pago{filteredAndSortedReminders.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('policy_number')}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>Póliza</span>
                    {sortConfig.key === 'policy_number' && (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('contractor')}
                >
                  <div className="flex items-center gap-2">
                    <span>Cliente</span>
                    {sortConfig.key === 'contractor' && (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('insurer')}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <span>Aseguradora</span>
                    {sortConfig.key === 'insurer' && (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('due_date')}
                >
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>Fecha de Pago</span>
                    {sortConfig.key === 'due_date' && (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100 transition-colors text-right"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center justify-end gap-2">
                    <DollarSign className="h-4 w-4" />
                    <span>Monto</span>
                    {sortConfig.key === 'amount' && (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedReminders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No hay pagos</p>
                    <p className="text-sm">No se encontraron pagos con los filtros seleccionados</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedReminders.map((reminder) => (
                  <TableRow 
                    key={reminder.id}
                    className={`hover:bg-slate-50 transition-colors ${
                      reminder.is_paid ? 'opacity-60' : ''
                    }`}
                  >
                    <TableCell className="font-medium">
                      {reminder.policy?.policy_number || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                          {(reminder.policy?.contractor?.name || reminder.policy?.contractor || 'N')?.charAt(0).toUpperCase()}
                        </div>
                        <span>{reminder.policy?.contractor?.name || reminder.policy?.contractor || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {reminder.policy?.insurer || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {reminder.policy?.policy_type || 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {formatDate(reminder.due_date)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">
                      {reminder.amount > 0 ? formatCurrency(reminder.amount, 'MXN') : '-'}
                    </TableCell>
                    <TableCell>
                      {reminder.is_paid ? (
                        <Badge className="bg-green-600 text-white border-0">
                          <Check className="h-3 w-3 mr-1" />
                          Pagado
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-600 border-red-300">
                          Pendiente
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!reminder.is_paid ? (
                          <Button
                            onClick={() => handleMarkAsPaid(reminder.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check size={16} className="mr-1" />
                            Marcar Pagado
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleUnmarkPaid(reminder.id)}
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            Desmarcar
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default PaymentReminders;
